/**
 * Field accessors that read either shape of a job.
 *
 * The bridge is supposed to send a normalised payload (flat `duration`,
 * `scripts`, `transcript_excerpt`, …) alongside the verbatim `raw` blob. The
 * on-disk job.json is laid out differently — duration lives under `download`,
 * the id is `id` not `external_id`, assets is an array rather than a count.
 * Every accessor prefers the normalised column and falls back into `raw`, so
 * the UI is correct whichever arrives.
 *
 * Two things genuinely cannot be recovered from `raw`: the transcript text and
 * the script list. job.json only points at `transcript.txt` and never mentions
 * the `script-*.md` files at all, so those are reported as pointers, not text.
 */

const rawOf = (job) => job?.raw ?? {};
const downloadOf = (job) => rawOf(job)?.download ?? {};
const contentOf = (job) => rawOf(job)?.content ?? {};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ??
  null;

export const jobExternalId = (job) =>
  firstDefined(job?.external_id, rawOf(job).id);

export const jobTitleText = (job) =>
  firstDefined(
    job?.title,
    rawOf(job).title,
    downloadOf(job).title,
    contentOf(job).title,
  );

export const jobDuration = (job) =>
  firstDefined(job?.duration, rawOf(job).duration, downloadOf(job).duration);

export const jobUploader = (job) =>
  firstDefined(
    rawOf(job).uploader,
    downloadOf(job).uploader,
    contentOf(job).author,
  );

export const jobAssetsCount = (job) => {
  if (typeof job?.assets_count === "number") return job.assets_count;
  const assets = rawOf(job).assets;
  return Array.isArray(assets) ? assets.length : 0;
};

export const jobSourceRef = (job) =>
  firstDefined(job?.source_ref, rawOf(job).source_ref);

export const jobError = (job) =>
  firstDefined(job?.error, rawOf(job).error, downloadOf(job).error);

export const jobCreatedAt = (job) =>
  firstDefined(job?.created_at, rawOf(job).created_at);

export const jobUpdatedAt = (job) =>
  firstDefined(job?.updated_at, rawOf(job).updated_at);

/**
 * The transcript, as much of it as exists in the payload.
 * @returns {null | {text: string|null, chars: number|null, file: string|null}}
 */
export const jobTranscript = (job) => {
  const text = firstDefined(
    job?.transcript_excerpt,
    rawOf(job).transcript_excerpt,
  );
  const download = downloadOf(job);
  const chars = download.transcript_chars ?? null;
  const file = download.transcript_file ?? null;

  if (!text && !chars && !file) return null;
  return { text, chars, file };
};

/**
 * @returns {{list: Array, mirrored: boolean}} `mirrored` is false when the
 * payload carries no scripts key at all — meaning "unknown", not "none".
 */
export const jobScripts = (job) => {
  const list = job?.scripts ?? rawOf(job).scripts;
  if (!Array.isArray(list)) return { list: [], mirrored: false };
  return { list, mirrored: true };
};

/**
 * The extracted article, for link_type=article jobs. Null for videos.
 */
export const jobArticle = (job) => {
  const content = contentOf(job);
  if (!content || content.ok !== true) return null;
  return {
    title: content.title ?? null,
    author: content.author ?? null,
    sitename: content.sitename ?? null,
    date: content.date ?? null,
    wordCount: content.word_count ?? null,
    url: content.extra?.url ?? null,
    textPath: content.text_path ?? null,
  };
};
